/* eslint-disable no-param-reassign */
import React, {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import Box from '@mui/material/Box';
import OutlinedInput from '@mui/material/OutlinedInput';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Chip from '@mui/material/Chip';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { Loader } from 'src/components/Loader';
import { ButtonApp } from 'src/components/Button';
import { IComplex } from 'src/typings/complexes';
import { useGetAuthData } from 'src/hooks';
import { patchUsersIdData, getUsersByDev } from 'src/api';
import { translations } from 'src/constants/translations';
import { IUsers } from 'src/typings/user';
import { AppComponentCheckbox } from 'src/components/AppCheckbox';
import { crosstableUrl, crosstableWithoutApi } from 'src/constants';
import CircularProgress from '@mui/material/CircularProgress';
import { ListSubheader } from '@mui/material';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { appIcon } from 'src/assets';

interface IAddExistingManager {
  complex: IComplex;
}

export const AddExistingManager: React.FC<IAddExistingManager> = ({
  complex,
}) => {
  const { getJWT } = useGetAuthData();
  const jwt = getJWT();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [currentValue, setCurrentValue] = useState<number[]>([]);
  const [users, setUsers] = useState<IUsers[] | null>(null);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [searchText, setSearchText] = useState('');
  const initRequestLink = `${crosstableUrl}/users?developers=${complex?.developers[0].id}&complexes_not_in[]=${complex.id}&properties[]=email&properties[]=id&properties[]=username&properties[]=iconUri&properties[complexes][]=id`;
  const [nextPage, setNextPage] = useState<string | null>(initRequestLink);

  const containsText = (text: string, search: string) => text.toLowerCase().indexOf(search.toLowerCase()) > -1;
  const displayedOptions = useMemo(
    () => users?.filter((option) => containsText(option.username, searchText)) || [],
    [searchText, users],
  );

  const funcFetch = async (nextPageUrl: string | null, isRefetch?: boolean) => {
    if (nextPage && complex?.developers[0] && nextPageUrl) {
      const { data } = await getUsersByDev(jwt, nextPageUrl);
      setUsers(!isRefetch ? [...users || [], ...data['hydra:member']] : data['hydra:member']);
      const tmpNext = data['hydra:view']['hydra:next'];
      if (tmpNext && nextPageUrl && tmpNext !== nextPageUrl) {
        setNextPage(`${crosstableWithoutApi}${data['hydra:view']['hydra:next']}`);
      } else {
        setNextPage(null);
      }
    }
    setLoadingMore(false);
  };

  useEffect(() => {
    funcFetch(nextPage);
  }, [jwt, complex]);

  const onSubmit = () => {
    if (displayedOptions) {
      currentValue.forEach((userId) => {
        const foundUser = displayedOptions.find((usr) => usr.id === userId);
        if (foundUser) {
          const userComplexes = foundUser.complexes.map((comp) => comp['@id']);
          userComplexes.push(complex['@id']);
          patchUsersIdData(jwt, foundUser.id, {
            complexes: userComplexes,
          });
        }
      });
      setCurrentValue([]);
      setTimeout(async () => {
        await funcFetch(initRequestLink, true);
        setSuccess(true);
      }, 200);
    }
  };

  const handleChange = (e:any) => {
    const { value } = e.target;
    if (users && value[value.length - 1] === 'all') {
      const usersArray = displayedOptions.map((user) => user.id);
      setCurrentValue(currentValue.length === usersArray.length ? [] : usersArray);
      return;
    }
    setCurrentValue(typeof value === 'string' ? value.split(',') : value);
  };

  const loadMoreItems = useCallback((e: any) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight;
    if (bottom <= 0 && !loadingMore) {
      setLoadingMore(true);
      funcFetch(nextPage);
    }
  }, [nextPage, loadingMore]);

  const getUserByID = useCallback((id: number) => users?.find((user) => user.id === id), [users]);

  if (!users) {
    return (
      <Box
        width="400px"
        height="100px"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Loader />
      </Box>
    );
  }

  return (
    <Box fontSize="18px" width="400px">
      <Box mb={6} alignItems="center">
        <Box width="100%" mb={2}>
          {translations.selectManagers}
        </Box>
        <Box width="100%">
          <FormControl variant="outlined">
            <Select
              variant="outlined"
              id="addExistingManagerLabel"
              multiple
              value={currentValue}
              onChange={handleChange}
              input={<OutlinedInput id="addExistingManagerSelect" />}
              MenuProps={{
                PaperProps: {
                  onScroll: loadMoreItems,
                },
                style: {
                  maxHeight: 500,
                },
              }}
              renderValue={(selected) => (
                <Box display="flex" flexWrap="wrap">
                  {selected.map((value) => (
                    <Box key={value} m="2px">
                      <Chip label={getUserByID(value)?.username || ''} />
                    </Box>
                  ))}
                </Box>
              )}
            >
              <ListSubheader
                style={{
                  paddingBottom: '6px',
                }}
              >
                <TextField
                  size="small"
                  autoFocus
                  placeholder="Type to search..."
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <img src={appIcon.iconSearch.default} alt="icon search" />
                      </InputAdornment>
                    ),
                  }}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Escape') {
                      e.stopPropagation();
                    }
                  }}
                />
              </ListSubheader>
              <MenuItem value="all">
                <AppComponentCheckbox
                  label="Select all"
                  isChecked={currentValue.length === Object.keys(displayedOptions).length}
                />
              </MenuItem>
              { displayedOptions.length ? displayedOptions.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  <AppComponentCheckbox
                    label={user.username}
                    isChecked={currentValue.includes(user.id)}
                  />
                </MenuItem>
              )) : (
                <Box
                  px={2}
                  style={{
                    pointerEvents: 'none',
                  }}
                >
                  {translations.noManagersFound}
                </Box>
              )}
              {loadingMore && (
                <MenuItem>
                  <Box width="100%" display="flex" justifyContent="center" alignItems="center">
                    <CircularProgress size="24px" />
                  </Box>
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box mb={6} display="flex" alignItems="center">
        <Box width="256px">
          <ButtonApp
            type="submit"
            color="primary"
            isCapitalize
            variant="contained"
            disabled={!currentValue.length}
            onClick={onSubmit}
            text={translations.add}
          />
        </Box>
      </Box>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
        <Alert onClose={() => setError('')} elevation={6} variant="filled" severity="error">
          {error}
        </Alert>
      </Snackbar>
      <Snackbar open={success} autoHideDuration={6000} onClose={() => setSuccess(false)}>
        <Alert onClose={() => setSuccess(false)} elevation={6} variant="filled" severity="success">
          Менеджеры добавлены
        </Alert>
      </Snackbar>
    </Box>
  );
};
